import { useEffect, useRef, useState } from "react";
import { NixiActionFrame } from "./NixiActionFrame";
import { NixiRiveRenderer } from "./NixiRiveRenderer";

export type NixiAnimationState =
  | "idle"
  | "blink"
  | "wave"
  | "wingFlutter"
  | "celebrate"
  | "feed"
  | "sleep";
export type NixiExpression =
  | "happy"
  | "excited"
  | "thinking"
  | "proud"
  | "encouraging"
  | "focused"
  | "sleepy"
  | "surprised";
export type NixiContext =
  | "home"
  | "morning"
  | "routine"
  | "task"
  | "feelings"
  | "help"
  | "celebration";
export type NixiColour = "lavender" | "pink" | "mint";
export type NixiVariant = "girl" | "boy";
export type NixiActivity =
  | "none"
  | "toilet"
  | "gettingDressed"
  | "dressed"
  | "brushingTeeth"
  | "eating"
  | "packingBag"
  | "shoes"
  | "preschool"
  | "snack"
  | "play"
  | "dinner"
  | "gettingPajamas"
  | "bedtime"
  | "bedtimeComplete"
  | "flyComplete";

type NixiCompanionProps = {
  state?: NixiAnimationState;
  expression?: NixiExpression;
  size?: "small" | "medium" | "large";
  context?: NixiContext;
  fallbackSrc?: string;
  riveSrc?: string;
  variant?: NixiVariant;
  className?: string;
  label?: string;
  colour?: NixiColour;
  activity?: NixiActivity;
};

const sizeClass = {
  small: "h-28 w-28 sm:h-32 sm:w-32",
  medium: "h-36 w-36 sm:h-44 sm:w-44",
  large:
    "h-[min(68vw,17rem)] w-[min(68vw,17rem)] sm:h-72 sm:w-72 md:h-80 md:w-80",
};

const riveAssetChecks = new Map<string, Promise<boolean>>();

function riveAssetExists(src: string) {
  const existing = riveAssetChecks.get(src);
  if (existing) return existing;
  const check = fetch(src, { method: "HEAD" })
    .then(
      (response) =>
        response.ok &&
        !response.headers.get("content-type")?.includes("text/html"),
    )
    .catch(() => false);
  riveAssetChecks.set(src, check);
  return check;
}

export function NixiCompanion({
  state = "idle",
  expression = "happy",
  size = "medium",
  context = "home",
  fallbackSrc,
  riveSrc,
  variant = "girl",
  className = "",
  label = "NIXI, your Marnixia companion",
  colour = "lavender",
  activity = "none",
}: NixiCompanionProps) {
  const resolvedFallbackSrc =
    fallbackSrc ||
    (variant === "boy" ? "/nixi-boy-approved.png" : "/nixi-girl-approved.webp");
  const isAnimatedSurface =
    context === "home" ||
    context === "morning" ||
    context === "routine" ||
    context === "task" ||
    context === "celebration";
  const routineRiveKey: Partial<Record<NixiActivity, string>> = {
    toilet: "toilet",
    gettingDressed: "dress",
    dressed: "pack",
    eating: "breakfast",
    brushingTeeth: "brush",
    packingBag: "pack",
    shoes: "shoes",
    preschool: "pack",
    snack: "breakfast",
    play: "play",
    dinner: "dinner",
    gettingPajamas: "pajamas",
    bedtime: "bedtime",
    bedtimeComplete: "bedtime",
    flyComplete: "complete",
  };
  const approvedRoutineRive = routineRiveKey[activity]
    ? `/nixi/nixi-routine-${routineRiveKey[activity]}-girl-v2.riv`
    : "/nixi/nixi-routine-play-girl-v2.riv";
  const usesActionMaster = [
    "gettingDressed",
    "dressed",
    "eating",
    "brushingTeeth",
    "packingBag",
    "shoes",
    "preschool",
    "snack",
    "play",
    "dinner",
    "flyComplete",
  ].includes(activity);
  const resolvedRiveSrc =
    riveSrc ||
    ((activity === "gettingPajamas" || activity === "bedtime" || activity === "bedtimeComplete") && variant === "girl" && colour === "lavender"
      ? "/nixi/nixi-girl-bedtime-v9.riv"
      : activity === "toilet" && variant === "girl" && colour === "lavender"
      ? "/nixi/nixi-girl-master-v7.riv"
      : (context === "home" || context === "celebration") &&
          variant === "girl" &&
          colour === "lavender"
        ? "/nixi/nixi-girl-master-v7.riv"
      : (context === "home" || context === "celebration") &&
          variant === "boy" &&
          colour === "lavender"
        ? "/nixi/nixi-boy-master-v1.riv"
      : context !== "home" && context !== "celebration" &&
          isAnimatedSurface &&
          variant === "boy" &&
          colour === "lavender"
        ? "/nixi/nixi-boy-master-v1.riv"
      : context !== "home" && context !== "celebration" &&
          isAnimatedSurface &&
          variant === "girl" &&
          colour === "lavender"
        ? usesActionMaster
          ? "/nixi/nixi-girl-actions-v8.riv"
          : approvedRoutineRive
      : variant === "boy"
      ? "/nixi/nixi-companion-boy.riv"
      : "/nixi/nixi-companion-girl.riv");
  const supportsRiveColour = colour === "lavender";
  const [imageFailed, setImageFailed] = useState(false);
  const [cutoutReady, setCutoutReady] = useState(false);
  const [riveReady, setRiveReady] = useState(false);
  const [riveFailed, setRiveFailed] = useState(false);
  const [riveAvailable, setRiveAvailable] = useState(false);
  const [reducedMotion, setReducedMotion] = useState(false);
  const canvasRef = useRef<HTMLCanvasElement>(null);

  useEffect(() => {
    const media = window.matchMedia("(prefers-reduced-motion: reduce)");
    const update = () => setReducedMotion(media.matches);
    update();
    media.addEventListener?.("change", update);
    return () => media.removeEventListener?.("change", update);
  }, []);

  useEffect(() => {
    setRiveReady(false);
    setRiveFailed(false);
    setRiveAvailable(false);
    let active = true;
    if (supportsRiveColour && resolvedRiveSrc)
      riveAssetExists(resolvedRiveSrc).then((available) => {
        if (active) setRiveAvailable(available);
      });
    return () => {
      active = false;
    };
  }, [resolvedRiveSrc, supportsRiveColour]);

  useEffect(() => {
    setImageFailed(false);
    setCutoutReady(false);

    const image = new Image();
    image.decoding = "async";
    image.src = resolvedFallbackSrc;
    image.onload = () => {
      const canvas = canvasRef.current;
      const context2d = canvas?.getContext("2d", { willReadFrequently: true });
      if (!canvas || !context2d) return;

      canvas.width = image.naturalWidth;
      canvas.height = image.naturalHeight;
      context2d.drawImage(image, 0, 0);

      try {
        const pixels = context2d.getImageData(
          0,
          0,
          canvas.width,
          canvas.height,
        );
        const visited = new Uint8Array(canvas.width * canvas.height);
        const queue = new Int32Array(canvas.width * canvas.height);
        let read = 0;
        let write = 0;
        const isBackdrop = (index: number) => {
          const offset = index * 4;
          const red = pixels.data[offset];
          const green = pixels.data[offset + 1];
          const blue = pixels.data[offset + 2];
          return (
            red > 224 &&
            green > 224 &&
            blue > 224 &&
            Math.max(red, green, blue) - Math.min(red, green, blue) < 30
          );
        };
        const add = (index: number) => {
          if (visited[index] || !isBackdrop(index)) return;
          visited[index] = 1;
          queue[write++] = index;
        };

        for (let x = 0; x < canvas.width; x += 1) {
          add(x);
          add((canvas.height - 1) * canvas.width + x);
        }
        for (let y = 0; y < canvas.height; y += 1) {
          add(y * canvas.width);
          add(y * canvas.width + canvas.width - 1);
        }
        while (read < write) {
          const index = queue[read++];
          const x = index % canvas.width;
          const y = Math.floor(index / canvas.width);
          pixels.data[index * 4 + 3] = 0;
          if (x > 0) add(index - 1);
          if (x < canvas.width - 1) add(index + 1);
          if (y > 0) add(index - canvas.width);
          if (y < canvas.height - 1) add(index + canvas.width);
        }
        if (colour !== "lavender") {
          const target = colour === "pink" ? [235, 99, 177] : [70, 184, 166];
          for (let index = 0; index < pixels.data.length; index += 4) {
            if (pixels.data[index + 3] === 0) continue;
            const red = pixels.data[index];
            const green = pixels.data[index + 1];
            const blue = pixels.data[index + 2];
            const lavenderPixel =
              blue > red * 1.04 && red > green * 1.04 && blue > 58;
            if (!lavenderPixel) continue;
            const light = (red + green + blue) / 510;
            const detail = Math.max(0.42, Math.min(1.28, light));
            pixels.data[index] = Math.min(255, target[0] * detail);
            pixels.data[index + 1] = Math.min(255, target[1] * detail);
            pixels.data[index + 2] = Math.min(255, target[2] * detail);
          }
        }
        context2d.putImageData(pixels, 0, 0);
        setCutoutReady(true);
      } catch {
        setCutoutReady(false);
      }
    };
    image.onerror = () => setImageFailed(true);

    return () => {
      image.onload = null;
      image.onerror = null;
    };
  }, [resolvedFallbackSrc, colour]);

  // Do not show the currently exported Rive canvas on a character surface: its
  // artboard has an opaque black matte and only generic timelines. Keep the
  // clean approved-art fallback visible until the transparent, state-machine
  // export replaces it. Non-character uses can still use a valid Rive export.
  const usesLayeredHomeRive =
    (context === "home" || context === "celebration") &&
    (variant === "girl" || variant === "boy") &&
    colour === "lavender";
  const usesLayeredRoutineRive =
    context !== "home" &&
    isAnimatedSurface &&
    (variant === "girl" || variant === "boy") &&
    colour === "lavender";
  const usesActionArtwork =
    isAnimatedSurface && !usesLayeredHomeRive && !usesLayeredRoutineRive;

  return (
    <figure
      className={`jnr-nixi jnr-nixi--${state} ${sizeClass[size]} ${className}`}
      data-animation-state={state}
      data-expression={expression}
      data-context={context}
      data-rive-source={supportsRiveColour ? resolvedRiveSrc : undefined}
      data-rive-ready={riveReady || undefined}
      data-colour={colour}
      data-variant={variant}
      data-activity={activity}
      aria-label={label}
    >
      {usesActionArtwork && (
        <NixiActionFrame
          variant={variant}
          colour={colour}
          activity={activity}
          home={context === "home"}
        />
      )}
      {!usesActionArtwork &&
        riveAvailable &&
        supportsRiveColour &&
        !riveFailed && (
          <NixiRiveRenderer
            key={usesLayeredRoutineRive ? `${resolvedRiveSrc}:${activity}` : resolvedRiveSrc}
            src={resolvedRiveSrc}
            state={state}
            expression={expression}
            context={context}
            colour={colour}
            variant={variant}
            activity={activity}
            reducedMotion={reducedMotion}
            label={label}
            onReady={() => setRiveReady(true)}
            onError={() => setRiveFailed(true)}
          />
        )}
      {!usesActionArtwork && (
        <span
          className={riveReady ? "hidden" : "contents"}
          aria-hidden={riveReady || undefined}
        >
          {!imageFailed ? (
            <>
              <canvas
                ref={canvasRef}
                className={`h-full w-full object-contain ${cutoutReady ? "block" : "hidden"}`}
                aria-hidden="true"
              />
              {!cutoutReady && (
                <img
                  src={resolvedFallbackSrc}
                  alt=""
                  className="h-full w-full object-contain mix-blend-multiply"
                  draggable={false}
                />
              )}
            </>
          ) : (
            <span className="grid h-full w-full place-items-center rounded-full border-2 border-violet-200 bg-violet-50 text-sm font-black text-violet-700">
              NIXI
            </span>
          )}
        </span>
      )}
      <span className="sr-only">{`State ${state}. Expression ${expression}.`}</span>
    </figure>
  );
}

